// InfoConsolidation.jsx / StepFourScreen.jsx
import React, { useState } from "react";
import {
  Box,
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import SummarizeIcon from "@mui/icons-material/Summarize";
import EditIcon from "@mui/icons-material/Edit";
import jsPDF from "jspdf";

/* =========================================================
   MAIN SCREEN
   ========================================================= */

const StepFourScreen = ({ ToHome }) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        py: 3,
        px: 2,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "95vw", maxWidth: 1500 }}>
        <Stack spacing={3}>
          <ConsolidatedQuoteInfoCard />
          <QuoteSummaryCard ToHome={ToHome} />
        </Stack>
      </Box>
    </Box>
  );
};

export default StepFourScreen;

/* =========================================================
   PORTION 1 – CONSOLIDATED QUOTE INFORMATION (LIFE)
   ========================================================= */

const ConsolidatedQuoteInfoCard = () => (
  <Card sx={{ borderRadius: 3 }}>
    <CardContent>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <FlightTakeoffIcon color="primary" fontSize="small" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Quote Information Consolidation (Life)
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review the AI-consolidated Life quote inputs and how they roll into the
        final quoting format:
      </Typography>

      {/* Two-column content */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ alignItems: "stretch" }}
      >
        {/* Left – AI Consolidated Data (verbatim Life bullets) */}
        <Box
          sx={{
            flex: 1,
            borderRadius: 3,
            bgcolor: "action.hover",
            p: 3,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              mb: 2,
            }}
          >
            AI Consolidated Data – Life
          </Typography>

          {/* Basic Life */}
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, display: "flex", gap: 0.5 }}
          >
            <span>✔</span> <span>Basic Life (Employer Paid)</span>
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body2">Benefit: 1× Salary</Typography>
            <Typography variant="body2">Maximum: $150,000</Typography>
            <Typography variant="body2">
              GI: Requested $150,000 (UW flagged)
            </Typography>
            <Typography variant="body2">
              Reductions: 65% @ 65, 50% @ 70
            </Typography>
            <Typography variant="body2">AD&amp;D: Yes</Typography>
          </Box>

          {/* Voluntary Life */}
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, display: "flex", gap: 0.5 }}
          >
            <span>✔</span> <span>Voluntary Life (Employee Paid)</span>
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body2">Increments: $10,000</Typography>
            <Typography variant="body2">Maximum requested: $300,000</Typography>
            <Typography variant="body2">
              Tobacco Dist. → Missing (awaiting broker data)
            </Typography>
            <Typography variant="body2">
              Dependent Life Plan → Pending broker clarification
            </Typography>
          </Box>

          {/* Underwriter Recommendation */}
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.5, display: "flex", gap: 0.5 }}
          >
            <span>✔</span> <span>Underwriter Recommendation</span>
          </Typography>
          <Box sx={{ pl: 3 }}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {`“Approve Basic Life at 1× salary to $150k with review load.
Vol Life GI should be capped at $100k unless participation improves.
Exclude 1099 workers from eligibility.”`}
            </Typography>
          </Box>
        </Box>

        {/* Right – Required Quoting Format (structured view) */}
        <Box
          sx={{
            flex: 1,
            borderRadius: 3,
            p: 3,
            bgcolor: "rgba(46, 125, 50, 0.06)", // soft green
            border: "1px solid",
            borderColor: "success.main",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
            Required Quoting Format – Life
          </Typography>

          {/* Basic Life block */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Basic Life (Employer Paid)
          </Typography>
          <KeyValue label="Benefit" value="1× annual salary" />
          <KeyValue label="Maximum Benefit" value="$150,000" />
          <KeyValue
            label="Guaranteed Issue"
            value="Up to $150,000 (subject to UW review flag)"
          />
          <KeyValue label="Age Reductions" value="65% @ age 65, 50% @ age 70" />
          <KeyValue label="AD&D" value="Included" />

          <Divider sx={{ my: 2 }} />

          {/* Voluntary Life block */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Voluntary Life (Employee Paid)
          </Typography>
          <KeyValue label="Increment" value="$10,000 increments" />
          <KeyValue label="Maximum Requested" value="$300,000" />
          <KeyValue
            label="Tobacco Distribution"
            value="Data missing – pending broker update"
          />
          <KeyValue
            label="Dependent Life"
            value="Plan design pending broker clarification"
          />

          <Divider sx={{ my: 2 }} />

          {/* Underwriter view in quote format */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Underwriter Position (for quote file)
          </Typography>
          <KeyValue
            label="Basic Life Decision"
            value="Approve 1× salary to $150,000 with review load"
          />
          <KeyValue
            label="Voluntary Life GI"
            value="Recommend GI cap at $100,000 unless participation improves"
          />
          <KeyValue
            label="Eligibility Notes"
            value="1099 workers to be excluded from eligibility"
          />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

/* Shared small component for label/value pairs */
const KeyValue = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

/* =========================================================
   PORTION 2 – QUOTE SUMMARY (INLINE EDITABLE + QUOTE MODAL)
   ========================================================= */

const QuoteSummaryCard = ({ ToHome }) => {
  const [openQuoteModal, setOpenQuoteModal] = useState(false);

  // These could be lifted up later if you want the quote text to be 100% dynamic
  const [totalMonthlyPremium, setTotalMonthlyPremium] = useState("$125,000");
  const [employerContribution, setEmployerContribution] = useState("80%");
  const [effectiveDate, setEffectiveDate] = useState("01/01/2026");
  const [perEmployeeCost, setPerEmployeeCost] = useState("$100");
  const [employeeContribution, setEmployeeContribution] = useState("20%");
  const [contractTerm, setContractTerm] = useState("12 months");

  const handleOpenQuote = () => {
    setOpenQuoteModal(true);
  };

  const handleCloseQuote = () => {
    setOpenQuoteModal(false);
  };

  const buildQuoteText = () => {
    return `
LIFE QUOTE SUMMARY
----------------------------

Client: Walmart Store #482
Coverage: Basic Life & Voluntary Life
Effective Date: ${effectiveDate}

BASIC LIFE (Employer Paid)
- Benefit: 1× annual salary
- Maximum Benefit: $150,000
- Guaranteed Issue: Up to $150,000 (UW review flagged)
- Age Reductions: 65% @ 65, 50% @ 70
- AD&D: Included

VOLUNTARY LIFE (Employee Paid)
- Increment: $10,000
- Maximum Requested: $300,000
- Tobacco Distribution: Data missing – pending broker update
- Dependent Life: Plan design pending broker clarification

UNDERWRITER POSITION
- Basic Life: Approve 1× salary to $150,000 with review load
- Voluntary Life GI: Recommend GI cap at $100,000 unless participation improves
- Eligibility Notes: Exclude 1099 workers from eligibility

FINANCIAL SUMMARY
- Total Monthly Premium: ${totalMonthlyPremium}
- Per Employee Cost: ${perEmployeeCost}
- Employer Contribution: ${employerContribution}
- Employee Contribution: ${employeeContribution}
- Contract Term: ${contractTerm}

Notes:
This is a simulated quote for demo purposes only.
    `.trim();
  };

  const handleDownloadPdf = () => {
    const doc = new jsPDF();

    const marginLeft = 10;
    const marginTop = 12;
    const maxWidth = 190;

    doc.setFontSize(14);
    doc.text("Life Quote – Walmart Store #482", marginLeft, marginTop);

    doc.setFontSize(11);
    const text = buildQuoteText();

    const splitText = doc.splitTextToSize(text, maxWidth);
    doc.text(splitText, marginLeft, marginTop + 8);

    doc.save("Life_Quote_Walmart_482.pdf");
  };

  const handleSendMail = () => {
    // Dummy action – in real app you'd integrate with email workflow
    alert("Dummy action: Quote email would be triggered here.");
  };

  return (
    <>
      <Card sx={{ borderRadius: 3, mb: 4 }}>
        <CardContent>
          {/* Header */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <SummarizeIcon color="primary" fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Quote Summary
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Review and edit the final Life quote details:
          </Typography>

          {/* Two-column layout */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 3, md: 8 }}
          >
            {/* Left column */}
            <Stack spacing={2.5} sx={{ flex: 1 }}>
              <EditableField
                label="Total Monthly Premium"
                value={totalMonthlyPremium}
                setValue={setTotalMonthlyPremium}
                emphasize
              />
              <EditableField
                label="Employer Contribution"
                value={employerContribution}
                setValue={setEmployerContribution}
              />
              <EditableField
                label="Effective Date"
                value={effectiveDate}
                setValue={setEffectiveDate}
              />
            </Stack>

            {/* Right column */}
            <Stack spacing={2.5} sx={{ flex: 1 }}>
              <EditableField
                label="Per Employee Cost"
                value={perEmployeeCost}
                setValue={setPerEmployeeCost}
                emphasize
              />
              <EditableField
                label="Employee Contribution"
                value={employeeContribution}
                setValue={setEmployeeContribution}
              />
              <EditableField
                label="Contract Term"
                value={contractTerm}
                setValue={setContractTerm}
              />
            </Stack>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Bottom right primary action */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={handleOpenQuote}
              variant="contained"
              color="primary"
              sx={{ borderRadius: 999, px: 3 }}
            >
              View Quote
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* QUOTE MODAL */}
      <Dialog
        open={openQuoteModal}
        onClose={handleCloseQuote}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          Life Quote – Walmart Store #482
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            bgcolor: "background.default",
          }}
        >
          {/* You can refine this layout later – this is a clean quote-like structure */}
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Client / Plan
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Walmart Store #482 – Basic & Voluntary Life
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Effective Date: {effectiveDate}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Basic Life (Employer Paid)
              </Typography>
              <KeyValue label="Benefit" value="1× annual salary" />
              <KeyValue label="Maximum Benefit" value="$150,000" />
              <KeyValue
                label="Guaranteed Issue"
                value="Up to $150,000 (UW review flagged)"
              />
              <KeyValue
                label="Age Reductions"
                value="65% @ age 65, 50% @ age 70"
              />
              <KeyValue label="AD&D" value="Included" />
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Voluntary Life (Employee Paid)
              </Typography>
              <KeyValue label="Increment" value="$10,000 increments" />
              <KeyValue label="Maximum Requested" value="$300,000" />
              <KeyValue
                label="Tobacco Distribution"
                value="Data missing – pending broker update"
              />
              <KeyValue
                label="Dependent Life"
                value="Plan design pending broker clarification"
              />
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Underwriter Position
              </Typography>
              <KeyValue
                label="Basic Life Decision"
                value="Approve 1× salary to $150,000 with review load"
              />
              <KeyValue
                label="Voluntary Life GI"
                value="Recommend GI cap at $100,000 unless participation improves"
              />
              <KeyValue
                label="Eligibility Notes"
                value="Exclude 1099 workers from eligibility"
              />
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Financial Summary
              </Typography>
              <KeyValue
                label="Total Monthly Premium"
                value={totalMonthlyPremium}
              />
              <KeyValue label="Per Employee Cost" value={perEmployeeCost} />
              <KeyValue
                label="Employer Contribution"
                value={employerContribution}
              />
              <KeyValue
                label="Employee Contribution"
                value={employeeContribution}
              />
              <KeyValue label="Contract Term" value={contractTerm} />
            </Box>

            <Typography variant="caption" color="text.secondary">
              * This quote is for demonstration purposes and not a binding offer
              of coverage.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" color="primary" onClick={handleSendMail}>
              Send Mail
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPdf}
            >
              Download PDF
            </Button>
          </Box>

          <Button
            color="secondary"
            variant="text"
            onClick={() => {
              handleCloseQuote();
              ToHome();
            }}
          >
            Close Opportunity
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/* Inline editable value component (updated to be controlled from parent) */
const EditableField = ({ label, value, setValue, emphasize = false }) => {
  const [editing, setEditing] = useState(false);

  const handleBlur = () => setEditing(false);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        {editing ? (
          <TextField
            variant="standard"
            size="small"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleBlur}
          />
        ) : (
          <Typography
            variant={emphasize ? "h6" : "body1"}
            sx={{
              fontWeight: emphasize ? 700 : 600,
              cursor: "text",
              minWidth: 60,
            }}
            onClick={() => setEditing(true)}
          >
            {value}
          </Typography>
        )}

        <IconButton
          size="small"
          onClick={() => setEditing(true)}
          sx={{ mt: editing ? 0.3 : 0 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
